import { SUPPLIER_CATEGORIES, OTHER_CATEGORY_VALUE, EVENT_TYPES, OTHER_EVENT_TYPE_VALUE } from '@/data/site';

const controlBase =
  'w-full rounded-md bg-background-100 border border-background-300 px-4 py-3 text-base text-foreground-950 placeholder:text-foreground-600 transition-colors duration-200 focus:border-primary-500';
const labelBase = 'block font-label text-sm uppercase tracking-[0.12em] text-foreground-700 mb-2';

const OTHER_VALUE = OTHER_CATEGORY_VALUE;

type CategorySelectProps = {
  value: string;
  onChange: (value: string) => void;
  otherValue: string;
  onOtherChange: (value: string) => void;
  error?: string;
};

export function CategorySelect({
  value,
  onChange,
  otherValue,
  onOtherChange,
  error,
}: CategorySelectProps) {
  return (
    <div>
      <label htmlFor="supplier_category" className={labelBase}>
        Supplier category <span className="text-primary-400">*</span>
      </label>
      <select
        id="supplier_category"
        name="supplier_category"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${controlBase} cursor-pointer`}
      >
        <option value="" disabled>
          Please choose&hellip;
        </option>
        {SUPPLIER_CATEGORIES.map((category) => (
          <option key={category} value={category}>
            {category}
          </option>
        ))}
      </select>
      {value === OTHER_VALUE ? (
        <input
          name="supplier_category_other"
          type="text"
          required
          value={otherValue}
          onChange={(e) => onOtherChange(e.target.value)}
          placeholder="Please specify the category"
          className={`${controlBase} mt-3`}
          aria-label="Please specify the supplier category"
        />
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-sm text-red-300">
          {error}
        </p>
      ) : null}
    </div>
  );
}

type CategoryCheckboxGroupProps = {
  values: string[];
  onChange: (values: string[]) => void;
  otherValue: string;
  onOtherChange: (value: string) => void;
  error?: string;
};

export function CategoryCheckboxGroup({
  values,
  onChange,
  otherValue,
  onOtherChange,
  error,
}: CategoryCheckboxGroupProps) {
  const toggle = (category: string) => {
    if (values.includes(category)) {
      onChange(values.filter((v) => v !== category));
    } else {
      onChange([...values, category]);
    }
  };

  return (
    <fieldset>
      <legend className={labelBase}>
        Supplier categories <span className="text-primary-400">*</span>{' '}
        <span className="normal-case tracking-normal text-foreground-600">(select all that apply)</span>
      </legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {SUPPLIER_CATEGORIES.map((category) => (
          <label
            key={category}
            htmlFor={`cat-${category}`}
            className="flex items-start gap-3 cursor-pointer"
          >
            <input
              id={`cat-${category}`}
              name="supplier_categories"
              type="checkbox"
              value={category}
              checked={values.includes(category)}
              onChange={() => toggle(category)}
              className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-[oklch(var(--primary-500))]"
            />
            <span className="text-sm leading-relaxed text-foreground-800">{category}</span>
          </label>
        ))}
      </div>
      {values.includes(OTHER_VALUE) ? (
        <input
          name="supplier_category_other"
          type="text"
          required
          value={otherValue}
          onChange={(e) => onOtherChange(e.target.value)}
          placeholder="Please specify the category"
          className={`${controlBase} mt-4`}
          aria-label="Please specify the supplier category"
        />
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-sm text-red-300">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

type EventTypeSelectProps = {
  value: string;
  onChange: (value: string) => void;
  otherValue: string;
  onOtherChange: (value: string) => void;
};

export function EventTypeSelect({
  value,
  onChange,
  otherValue,
  onOtherChange,
}: EventTypeSelectProps) {
  return (
    <div>
      <label htmlFor="event_type" className={labelBase}>
        Event type <span className="text-primary-400">*</span>
      </label>
      <select
        id="event_type"
        name="event_type"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${controlBase} cursor-pointer`}
      >
        <option value="" disabled>
          Please choose&hellip;
        </option>
        {EVENT_TYPES.map((type) => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </select>
      {value === OTHER_EVENT_TYPE_VALUE ? (
        <input
          name="event_type_other"
          type="text"
          required
          value={otherValue}
          onChange={(e) => onOtherChange(e.target.value)}
          placeholder="Please specify the event type"
          className={`${controlBase} mt-3`}
          aria-label="Please specify the event type"
        />
      ) : null}
    </div>
  );
}

export { OTHER_VALUE };